/**
 * Session-scoped UI preferences.
 *
 * sessionStorage is fine here: the "NO localStorage anywhere" rule in
 * maps-session.service.ts is about AUTH (the session token lives in an
 * HttpOnly cookie) — a results-per-page preference carries nothing sensitive
 * and deliberately resets when the tab closes.
 */

export const PAGE_SIZE_OPTIONS = [5, 10, 20, 50] as const;
export type PageSizeOption = (typeof PAGE_SIZE_OPTIONS)[number];

/** Mockup default stays 10 — the 5 option exists for small screens, not as
 *  the default (PAGE_SIZE_OPTIONS[0] is no longer the default). */
export const DEFAULT_PAGE_SIZE: PageSizeOption = 10;

const PAGE_SIZE_KEY = 'maps.pageSize';

export function IsPageSizeOption(value: number): value is PageSizeOption {
    return (PAGE_SIZE_OPTIONS as readonly number[]).includes(value);
}

/** Read the saved results-per-page choice; null when unset/invalid/unavailable. */
export function ReadSessionPageSize(): PageSizeOption | null {
    try {
        const raw = sessionStorage.getItem(PAGE_SIZE_KEY);
        if (raw === null) return null;
        const parsed = Number(raw);
        return IsPageSizeOption(parsed) ? parsed : null;
    } catch {
        // Storage can throw (e.g. privacy modes) — treat as "no preference".
        return null;
    }
}

export function WriteSessionPageSize(size: PageSizeOption): void {
    try {
        sessionStorage.setItem(PAGE_SIZE_KEY, String(size));
    } catch {
        // Best-effort: losing the preference is acceptable.
    }
}
